import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  Clock,
  FolderTree,
  FlaskConical,
  Layers,
  Lock,
  Network,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import TopologyPreviewModal from "./topology/TopologyPreviewModal";
// MULTIVM-DUMMY-TOPOLOGY: temporary "coming soon" topology button for Multi-VM labs
import MultiVmDummyTopology from "./topology/MultiVmDummyTopology";

function DifficultyGaugeIcon({ difficulty, className = "" }) {
  const normalized = (difficulty || "").trim().toLowerCase();

  const level =
    normalized === "easy" || normalized === "low"
      ? "low"
      : normalized === "intermediate" || normalized === "medium"
        ? "medium"
        : normalized === "advanced" ||
            normalized === "hard" ||
            normalized === "high"
          ? "high"
          : null;

  if (!level) {
    return (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={className}
        fill="none"
      >
        <path
          d="M4 16a8 8 0 0 1 16 0"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.35"
        />
        <circle cx="12" cy="16" r="2" fill="currentColor" opacity="0.55" />
      </svg>
    );
  }

  const levelStyles = {
    low: {
      arcColor: "#34d399",
      needleRotation: "rotate(-35 12 16)",
    },
    medium: {
      arcColor: "#facc15",
      needleRotation: "rotate(0 12 16)",
    },
    high: {
      arcColor: "#ef4444",
      needleRotation: "rotate(35 12 16)",
    },
  };

  const { arcColor, needleRotation } = levelStyles[level];

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      fill="none"
    >
      <path
        d="M4 16a8 8 0 0 1 16 0"
        stroke="#d1d5db"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M4 16a8 8 0 0 1 16 0"
        stroke={arcColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="14 50"
      />
      <path
        d="M12 16L18 11"
        stroke="#9ca3af"
        strokeWidth="1.8"
        strokeLinecap="round"
        transform={needleRotation}
      />
      <circle cx="12" cy="16" r="2.2" fill="#9ca3af" />
      <circle cx="12" cy="16" r="0.95" fill="#f8fafc" />
    </svg>
  );
}

function splitTitleOnTopLevelSeparator(title) {
  const safeTitle = title || "";
  let depth = 0;

  for (let i = 0; i < safeTitle.length - 2; i += 1) {
    const char = safeTitle[i];

    if (char === "(") {
      depth += 1;
      continue;
    }

    if (char === ")") {
      depth = Math.max(0, depth - 1);
      continue;
    }

    if (depth === 0 && safeTitle.slice(i, i + 3) === " - ") {
      return [safeTitle.slice(0, i), safeTitle.slice(i + 3)];
    }
  }

  return [safeTitle];
}

const getCardImage = (node) => {
  const slug = (node.slug || node.id || "").toLowerCase();
  const title = (node.title || "").toLowerCase();
  
  if (slug.includes("build-your-own") || title.includes("build your own")) {
    return "/assets/tracks/build_your_own.png";
  }
  if (slug.includes("scywf") || title.includes("scywf")) {
    return "/assets/tracks/scywf.png";
  }
  if (slug.includes("emerging-threats") || title.includes("emerging threats")) {
    return "/assets/tracks/emerging_threats.png";
  }
  if (slug.includes("ai-") || slug === "ai" || title.includes("artificial intelligence") || title.includes(" ai ")) {
    return "/assets/tracks/ai.png";
  }
  if (slug.includes("grc") || title.includes("grc") || title.includes("governance")) {
    return "/assets/tracks/grc.png";
  }
  if (slug.includes("ot") || title.includes("ot ") || title.includes("operational technology")) {
    return "/assets/tracks/ot.png";
  }
  if (slug.includes("quantum") || title.includes("quantum")) {
    return "/assets/tracks/quantum.png";
  }
  
  return "/assets/tracks/build_your_own.png";
};

export default function NodeCard({ node, inMultiVmCategory = false }) {
  const navigate = useNavigate();
  const [showTopologyPreview, setShowTopologyPreview] = useState(false);

  const handleClick = () => {
    if (isPendingScenario) return; // Prevent navigation for pending scenarios
    const targetId = node.slug || node.id;
    if (isLab) {
      navigate(`/lab/${targetId}`, {
        state: {
          title: node.title,
          cve: node.cve,
          description: node.description,
          difficulty: node.difficulty,
          time_estimate: node.time_estimate,
          learning_objectives: node.learning_objectives,
          // Carry the saved topology so the Lab page can show the Topology tab
          // instantly on navigation, before the catalog tree finishes loading.
          topology_json: node.topology_json,
        },
      });
      return;
    }

    navigate(`/boxes/${targetId}`);
  };

  const isLab = node.type === "scenario";
  const isCategory = node.type === "category";
  const isPendingScenario = node.type === "pending_scenario";

  const isMultiVmCategory =
    isCategory &&
    String(node.title || "")
      .trim()
      .toLowerCase()
      .includes("multi-vm");

  const childCount = node.children?.length || 0;
  const titleParts = splitTitleOnTopLevelSeparator(node.title);
  const headline = titleParts[0] || node.title || "Untitled";
  const description = isPendingScenario
    ? `This scenario is currently being generated. Status: ${node.state || "Pending"}. Check back soon!`
    : !isCategory && node.description
      ? node.description
      : titleParts.length > 1
        ? titleParts.slice(1).join(" - ")
        : isCategory
          ? "Comprehensive cybersecurity training designed to build foundational and advanced skills through hands-on labs, interactive challenges, and real-world scenarios."
          : "Hands-on scenario to practice real-world cybersecurity skills.";
  const badgeLabel = isCategory
    ? `${childCount} MODULES`
    : isPendingScenario
      ? "PENDING"
      : isLab
        ? "BOX"
        : "PATH";
  const hasTopology = isLab && !!node.topology_json;
  const topologyNodes = isLab ? node.topology_json?.nodes || [] : [];
  const topologyEdges = isLab ? node.topology_json?.edges || [] : [];
  const difficulty = isLab ? node.difficulty || null : null;
  const timeEstimate = isLab ? node.time_estimate || null : null;
  const normalizedDifficulty = (difficulty || "").trim().toLowerCase();
  const difficultyColor =
    normalizedDifficulty === "easy" || normalizedDifficulty === "low"
      ? "text-emerald-500"
      : normalizedDifficulty === "intermediate" ||
          normalizedDifficulty === "medium"
        ? "text-amber-400"
        : normalizedDifficulty === "advanced" ||
            normalizedDifficulty === "hard" ||
            normalizedDifficulty === "high"
          ? "text-rose-500"
          : "text-(--color-text-muted)";
  const ctaLabel = isLab
    ? "Go to Box"
    : isCategory
      ? "Explore Track"
      : "Explore Track";

  const handleOpenTopologyPreview = (event) => {
    event?.stopPropagation();
    setShowTopologyPreview(true);
  };

  const handleCloseTopologyPreview = () => {
    setShowTopologyPreview(false);
  };

  const icon = useMemo(() => {
    if (isPendingScenario)
      return (
        <Clock className="h-5 w-5 text-gray-400 animate-pulse" />
      );
    if (isLab)
      return <FlaskConical className="h-5 w-5 text-(--color-primary)" />;
    return node.children?.length ? (
      <FolderTree className="h-5 w-5 text-(--color-primary)" />
    ) : (
      <Layers className="h-5 w-5 text-(--color-primary)" />
    );
  }, [isPendingScenario, isLab, node.children?.length]);

  return (
    <div
      role="button"
      tabIndex={isPendingScenario ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      style={
        isMultiVmCategory
          ? { boxShadow: "inset 0 0 55px -8px rgba(139,92,246,0.28)" }
          : undefined
      }
      className={`group relative w-full h-full text-left p-6 flex flex-col gap-4 overflow-hidden transition-all focus:outline-none qv-card qv-noise ${
        isPendingScenario
          ? "opacity-60 cursor-not-allowed border-gray-300 dark:border-(--color-border) bg-(--color-bg)/30 dark:bg-(--color-bg-card)"
          : isMultiVmCategory
            ? "focus:ring-2 focus:ring-violet-400/30 border-violet-400/40 bg-violet-400/5 dark:bg-violet-500/10 hover:border-violet-400/60"
            : "focus:ring-2 focus:ring-(--color-primary)/30 bg-(--color-bg)/50 dark:bg-(--color-card)"
      }`}
    >
      {/* Background Cover Image with Scale effect */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
        style={{ backgroundImage: `url('${getCardImage(node)}')` }}
      />

      {/* Dark gradient overlay to keep text legible over the image */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/70 to-slate-950/50 z-0" />

      {/* Lock overlay for pending scenarios */}
      {isPendingScenario && (
        <div className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full z-10 bg-slate-950/50 border border-slate-800/50">
          <Lock className="h-4 w-4 text-gray-400" />
        </div>
      )}

      {/* COLLAPSED HORIZONTAL STRIP CONTENT (Always visible in collapsed state, fades out on hover) */}
      <div className="absolute inset-x-0 top-0 h-[80px] md:h-[90px] flex items-center justify-between px-6 z-10 pointer-events-none transition-all duration-300 ease-in-out group-hover:opacity-0 group-hover:pointer-events-none">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center border border-slate-700/45 bg-slate-950/80 backdrop-blur-xs">
            {icon}
          </div>
          <div className="min-w-0">
            <h3
              className={`text-base md:text-lg font-bold text-white tracking-tight ${
                isPendingScenario ? "leading-snug line-clamp-2" : "leading-none"
              }`}
            >
              {headline}
            </h3>
            {isLab && (
              <span className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mt-1.5 block">
                Scenario Box
              </span>
            )}
          </div>
        </div>
        <div
          className={`flex flex-wrap items-center gap-2 ${isPendingScenario ? "pr-10" : ""}`}
        >
          {isMultiVmCategory && (
            <span className="inline-flex items-center gap-1 rounded-full border border-violet-400/50 bg-violet-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-300">
              <Sparkles className="h-2.5 w-2.5" />
              Beta
            </span>
          )}
          {hasTopology && (
            <button
              type="button"
              title="Click to preview topology"
              className="cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-(--color-primary)/50 bg-(--color-primary)/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.35em] text-(--color-primary) transition hover:bg-(--color-primary)/20 hover:border-(--color-primary) hover:shadow-[0_0_8px_rgba(0,200,0,0.25)] active:scale-95"
              onClick={handleOpenTopologyPreview}
            >
              <Network className="h-3 w-3" />
              Topology
            </button>
          )}
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-(--color-text-muted)">
            <span className="h-2.5 w-px bg-(--color-border)" />
            {badgeLabel}
          </span>
          <ChevronRight className="h-4 w-4 text-slate-500" />
        </div>
      </div>

      {/* EXPANDED rich content overlay (Fades and slides up on hover) */}
      <div
        className="w-full p-5 md:p-6 flex flex-col justify-end z-10 relative pointer-events-none group-hover:pointer-events-auto transition-all duration-500 ease-out
          opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0
        "
      >
        <div className="space-y-3">
          {/* Header row: Badge and statistics */}
          <div className="flex items-center gap-2 text-[11px] qv-cyber uppercase font-bold tracking-wider text-(--color-primary)">
            {icon}
            <span className="ml-1 border-r border-(--color-primary)/20 pr-2">{badgeLabel}</span>
            {isLab && timeEstimate && (
              <span className="text-slate-350 font-semibold flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {timeEstimate}
              </span>
            )}
            {!isLab && (
              <span className="text-slate-350 font-semibold">{childCount} sub-items</span>
            )}
          </div>

          {/* Title and specifications */}
          <div className="space-y-1">
            <h3
              className={`text-xl md:text-2xl lg:text-3xl font-extrabold text-white tracking-tight leading-[1.1] drop-shadow-[0_4px_15px_rgba(0,0,0,0.8)] qv-serif ${
                isPendingScenario ? "line-clamp-2" : ""
              }`}
            >
              {headline}
            </h3>
          </div>

          {/* Description */}
          <p className="text-xs md:text-sm text-white/85 leading-relaxed max-w-3xl drop-shadow-[0_1px_4px_rgba(0,0,0,0.7)] line-clamp-2">
            {description}
          </p>

          {/* Bottom stats and execution actions */}
          <div className="flex items-center justify-between gap-4 pt-3 border-t border-slate-800/35">
            {isLab ? (
              <div className="flex items-center gap-2 text-[11px]">
                <DifficultyGaugeIcon difficulty={difficulty} className="h-4.5 w-4.5 shrink-0" />
                <span className={`font-semibold ${difficulty ? difficultyColor : "text-slate-400"}`}>
                  {difficulty ?? "TBD"}
                </span>
              </div>
            ) : (
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">
                QUANTUM VANGUARD PLATFORM
              </div>
            )}

            <div className="flex items-center gap-2">
              {hasTopology && (
                <button
                  type="button"
                  className="cursor-pointer inline-flex items-center justify-center p-1.5 rounded-lg border border-slate-700 bg-slate-900/60 text-slate-300 hover:text-(--color-primary) hover:border-(--color-primary) transition"
                  onClick={handleOpenTopologyPreview}
                  title="Preview Topology"
                >
                  <Network className="h-4.5 w-4.5" />
                </button>
              )}
              {/* MULTIVM-DUMMY-TOPOLOGY: show placeholder topology button for Multi-VM labs without a real topology */}
              {isLab && inMultiVmCategory && !hasTopology && (
                <MultiVmDummyTopology title={headline} />
              )}
              <button
                type="button"
                disabled={isPendingScenario}
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-(--color-primary) bg-(--color-primary) px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-(--color-text-on-primary) hover:bg-transparent hover:text-(--color-primary) transition-all duration-300 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
              >
                {isPendingScenario ? "Coming Soon" : ctaLabel}
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <TopologyPreviewModal
        show={showTopologyPreview}
        title={headline}
        nodes={topologyNodes}
        edges={topologyEdges}
        onClose={handleCloseTopologyPreview}
        showInfo={false}
      />
    </div>
  );
}